import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Play, ChevronRight, ChevronLeft, X, Users, Calendar, ClipboardCheck,
  AlertTriangle, DollarSign, Sparkles, CheckCircle2, ExternalLink, Rocket
} from 'lucide-react';

const DEMO_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to GetCare 360',
    subtitle: 'This demo shows a typical NDIS provider setup with real operational scenarios.',
    description: "We'll walk you through how your team would manage:\n• Participants & Support Snapshots\n• Rostering & Scheduling\n• Compliance & Incidents\n• Invoicing & Claims\n• AI-Powered Insights",
    icon: Rocket,
    route: null,
    highlight: null,
  },
  {
    id: 'participant',
    title: 'Participant Profile',
    subtitle: 'Your staff understand participant needs in under 10 seconds.',
    description: "The Support Snapshot gives frontline workers everything they need — communication style, mobility, alerts, assistance required, and upcoming schedule — all in one glanceable card.",
    icon: Users,
    route: null, // Will be set dynamically to demo participant
    highlight: 'v3-snapshot-hero',
  },
  {
    id: 'roster',
    title: 'Visual Calendar & Rostering',
    subtitle: 'Drag-and-drop rostering with real-time conflict detection.',
    description: "Plan shifts across your entire workforce. See coverage gaps, manage recurring shifts, and handle swap requests — all from one visual interface.",
    icon: Calendar,
    route: '/roster/visual',
    highlight: null,
  },
  {
    id: 'timesheets',
    title: 'Automated Timesheets',
    subtitle: 'No manual timesheets. Everything generated from actual shifts.',
    description: "When staff complete shifts, timesheets are auto-generated. Managers review and approve with one click. Exception handling catches discrepancies automatically.",
    icon: ClipboardCheck,
    route: '/timesheets/approval',
    highlight: null,
  },
  {
    id: 'incidents',
    title: 'NDIS-Compliant Incident Tracking',
    subtitle: 'Full audit trail for every incident.',
    description: "Create, track, and resolve incidents with built-in NDIS compliance. Every action is logged for audit readiness. Intelligence dashboards highlight patterns.",
    icon: AlertTriangle,
    route: '/compliance/incidents',
    highlight: null,
  },
  {
    id: 'finance',
    title: 'Invoicing & Claims',
    subtitle: 'Convert completed shifts directly into NDIS claims.',
    description: "Auto-generate invoices from approved timesheets. Track claim submissions, reconcile payments, and forecast funding utilisation — reducing admin by up to 70%.",
    icon: DollarSign,
    route: '/finance/pipeline',
    highlight: null,
  },
  {
    id: 'insights',
    title: 'AI-Powered Insights',
    subtitle: 'Reduce admin work and identify risks early.',
    description: "GetCare 360's AI analyses your operations to surface recommendations, compliance risks, workforce patterns, and funding optimisation opportunities.",
    icon: Sparkles,
    route: '/ai/intelligence',
    highlight: null,
  },
  {
    id: 'complete',
    title: "You've seen GetCare 360 in action",
    subtitle: 'Ready to transform your NDIS operations?',
    description: null,
    icon: CheckCircle2,
    route: null,
    highlight: null,
    isFinal: true,
  },
];

export function DemoModeProvider({ children }) {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [demoParticipantId, setDemoParticipantId] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const currentStep = DEMO_STEPS[step];

  const startDemo = useCallback(async () => {
    // Seed demo data first
    setSeeding(true);
    try {
      await api.post('/demo/seed');
      const res = await api.get('/demo/participants');
      const participants = res.data?.data || [];
      if (participants.length > 0) {
        setDemoParticipantId(participants[0].id);
        // Update step 1 route with the demo participant
        DEMO_STEPS[1].route = `/participants/${participants[0].id}`;
      }
    } catch (err) {
      toast.error('Failed to prepare demo data');
    } finally {
      setSeeding(false);
    }
    setStep(0);
    setActive(true);
  }, []);

  const nextStep = useCallback(() => {
    const next = Math.min(step + 1, DEMO_STEPS.length - 1);
    setStep(next);
    if (DEMO_STEPS[next].route) {
      navigate(DEMO_STEPS[next].route);
    }
  }, [step, navigate]);

  const prevStep = useCallback(() => {
    const prev = Math.max(step - 1, 0);
    setStep(prev);
    if (DEMO_STEPS[prev].route) {
      navigate(DEMO_STEPS[prev].route);
    }
  }, [step, navigate]);

  const exitDemo = useCallback(() => {
    setActive(false);
    setStep(0);
  }, []);

  // Expose demo trigger globally
  useEffect(() => {
    window.__startGetCareDemo = startDemo;
    return () => { delete window.__startGetCareDemo; };
  }, [startDemo]);

  return (
    <>
      {children}

      {/* Demo Overlay Dialog */}
      {active && currentStep && (
        <DemoStepDialog
          step={currentStep}
          stepIndex={step}
          totalSteps={DEMO_STEPS.length}
          onNext={nextStep}
          onPrev={prevStep}
          onExit={exitDemo}
          seeding={seeding}
        />
      )}
    </>
  );
}

function DemoStepDialog({ step, stepIndex, totalSteps, onNext, onPrev, onExit, seeding }) {
  const Icon = step.icon;

  return (
    <Dialog open={true} onOpenChange={onExit}>
      <DialogContent className="max-w-lg" data-testid="demo-step-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg">{step.title}</DialogTitle>
              {step.subtitle && <DialogDescription className="text-sm mt-0.5">{step.subtitle}</DialogDescription>}
            </div>
            <Badge variant="secondary" className="text-[10px]">{stepIndex + 1}/{totalSteps}</Badge>
          </div>
        </DialogHeader>

        {step.description && (
          <div className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed py-2">
            {step.description}
          </div>
        )}

        {/* Final step: CTA buttons */}
        {step.isFinal && (
          <div className="space-y-3 py-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <Card><CardContent className="py-3"><CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-emerald-600" /><p className="text-xs font-medium">Reduce admin time</p></CardContent></Card>
              <Card><CardContent className="py-3"><CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-emerald-600" /><p className="text-xs font-medium">Improve compliance</p></CardContent></Card>
              <Card><CardContent className="py-3"><CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-emerald-600" /><p className="text-xs font-medium">Scale operations</p></CardContent></Card>
            </div>
            <div className="flex gap-2 pt-2">
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => window.open('https://getcare360.com/book-setup', '_blank')} data-testid="demo-book-setup">
                <Calendar className="w-4 h-4 mr-1.5" /> Book Setup
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => window.open('https://getcare360.com/contact', '_blank')} data-testid="demo-talk-to-us">
                <ExternalLink className="w-4 h-4 mr-1.5" /> Talk to Us
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between pt-2">
          <div className="flex gap-1">
            {/* Progress dots */}
            {DEMO_STEPS.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === stepIndex ? 'bg-primary' : i < stepIndex ? 'bg-primary/40' : 'bg-muted'}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onExit} data-testid="demo-exit">
              <X className="w-3.5 h-3.5 mr-1" /> Exit
            </Button>
            {stepIndex > 0 && (
              <Button variant="outline" size="sm" onClick={onPrev} data-testid="demo-prev">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            )}
            {!step.isFinal && (
              <Button size="sm" onClick={onNext} disabled={seeding} data-testid="demo-next">
                {seeding ? 'Preparing...' : stepIndex === 0 ? 'Start Demo' : 'Next'}
                <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Trigger button for the sidebar/header */
export function DemoModeTrigger() {
  const handleClick = () => {
    if (window.__startGetCareDemo) {
      window.__startGetCareDemo();
    }
  };

  return (
    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={handleClick} data-testid="demo-mode-trigger">
      <Play className="w-3 h-3" /> Demo
    </Button>
  );
}
